import _get from 'lodash/get';
import _cloneDeep from 'lodash/cloneDeep';
import _round from 'lodash/round';
import moment from 'moment'
import { celsiusToFahrenheit, fahrenheitToCelsius, getTemperatureUnitSuffix } from 'services/utils';
import { MEASUREMENT_SYSTEM_CHANGED_EVENT } from 'services/StudioEvents';
import { parseSampleInput, buildSamples, randomSampleInput } from '../event-samples';

// Assumed to match the temperature sensor's limit — the proto doesn't specify one
const MAX_SAMPLES = 30;

/* @ngInject */
export default class EmulatorVirtualGlycolBufferController {
    constructor(
        EventEmitter,
        $rootScope,
        UserPreferencesManager
    ) {
        this.EventEmitter = EventEmitter;
        this.$rootScope = $rootScope;
        this.UserPreferencesManager = UserPreferencesManager;
    }

    $onInit() {
        this.determineValues()

        this.inputSamplesText = ''
        this.samples = []
        this.previewVisible = false
        this.shouldIncludeSamples = false

        this.deregister = this.$rootScope.$on(MEASUREMENT_SYSTEM_CHANGED_EVENT, () => {
            this.determineValues()
        })
    }

    determineValues() {
        this.volumeOptions = [{ label: '300 ml', value: 300 }]
        this.event = {
            virtualGlycolBuffer: {
                value: this.convertedTemperature(_get(this.device, 'reported.virtualGlycolBuffer.samples.0.value',
                    _get(this.device, 'reported.temperature.value', 0))),
                volume: this.volumeOptions[0].value
            }
        }
        this.min = this.convertedTemperature(-40)
        this.max = this.convertedTemperature(85)
        this.unitSuffix = getTemperatureUnitSuffix()
    }

    updateSamples() {
        const eventValue = this.event.virtualGlycolBuffer.value
        if (eventValue === null || eventValue === undefined) {
            return
        }

        const { sanitisedInput, samples } = parseSampleInput(this.inputSamplesText, eventValue, MAX_SAMPLES)
        this.inputSamplesText = sanitisedInput
        this.samples = samples

        const { samples: output, formattedSampleInterval } = buildSamples(samples, { useDummyTimestamps: true })
        this.output = output
        this.formattedSampleInterval = formattedSampleInterval

        this.outputJSON = JSON.stringify({virtualGlycolBuffer: { volume: this.event.virtualGlycolBuffer.volume, updateTime: this.output[0].sampleTime, samples: this.output}}, null, 4)
    }

    addRandomSamples(count) { // Populates the comma separated input field with a specific number of values (centered around event value)
        this.inputSamplesText = randomSampleInput(count, this.event.virtualGlycolBuffer.value)
        this.updateSamples()
    }

    toggleEventPreview() {
        this.previewVisible = !this.previewVisible
    }

    convertedTemperature(temperature) {
        return this.UserPreferencesManager.useFahrenheit
            ? _round(celsiusToFahrenheit(temperature), 2)
            : temperature;
    }

    ensureCelsius(temperature) {
        return this.UserPreferencesManager.useFahrenheit
            ? _round(fahrenheitToCelsius(temperature), 2)
            : temperature;
    }

    // Real devices derive the buffer from the temperature samples, so the graph overlays
    // the two series by exact timestamp. Reuse the device's latest temperature timestamps
    // here, otherwise emulated events never line up with the temperature data.
    temperatureSampleTimes() {
        const sampleTimes = _get(this.device, 'reported.temperature.samples', [])
            .map(sample => sample.sampleTime)
            .filter(Boolean)

        if (sampleTimes.length > 0) {
            return sampleTimes
        }

        const updateTime = _get(this.device, 'reported.temperature.updateTime', null)
        return updateTime ? [updateTime] : []
    }

    sendEvent() {
        const sendEvent = _cloneDeep(this.event)
        const celsiusValue = this.ensureCelsius(sendEvent.virtualGlycolBuffer.value)
        const sampleTimes = this.temperatureSampleTimes()
        const now = moment().toISOString()

        sendEvent.virtualGlycolBuffer.samples = [{ value: celsiusValue, sampleTime: sampleTimes[0] || now }]

        if (this.shouldIncludeSamples) {
            // Send values as Celsius, keep local state if Fahrenheit
            const { samples } = buildSamples(this.samples, { transformValue: value => this.ensureCelsius(value) })
            if (samples.length > 1) {
                sendEvent.virtualGlycolBuffer.samples = samples.map((sample, index) => ({
                    ...sample,
                    sampleTime: sampleTimes[index] || sample.sampleTime
                }))
            }
        }

        // Ensure timestamp of event and first sample is equal
        sendEvent.virtualGlycolBuffer.updateTime = sendEvent.virtualGlycolBuffer.samples[0].sampleTime

        this.onPublishEvent(
            this.EventEmitter({
                event: sendEvent
            })
        )
    }

    $onDestroy() {
        if (this.deregister) {
            this.deregister();
        }
    }
}
